import {
  DatePicker,
  FormField,
  NumberStepper,
  SegmentedControl,
  Select,
  Textarea,
  todayIso,
  useToast,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { formErrors } from '../../../lib/errors';
import { isQueued } from '../../offline/offline';
import {
  useColonies,
  useFeedTypes,
  useRecordFeeding,
  useUpdateFeeding,
  type Feeding,
  type FeedingFields,
  type FeedingPurpose,
  type FeedUnit,
} from '../api';
import { formatAmount } from '../format';
import { ColonyPicker } from './ColonyPicker';
import styles from './forms.module.css';

const PURPOSES: FeedingPurpose[] = ['winter', 'emergency', 'stimulative', 'nucleus'];

export interface FeedingDialogProps {
  open: boolean;
  onClose: () => void;
  feeding?: Feeding;
  colonyIds?: string[];
  onRecorded?: () => void;
}

/** Records a feeding for several colonies (FED-001) or edits one (FED-003). */
export function FeedingDialog({
  open,
  onClose,
  feeding,
  colonyIds,
  onRecorded,
}: FeedingDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const feedTypes = useFeedTypes(false, open);
  const colonies = useColonies({ status: 'active' }, open && !feeding);
  const record = useRecordFeeding();
  const update = useUpdateFeeding();
  const mutation = feeding ? update : record;
  const [selected, setSelected] = useState<string[]>(colonyIds ?? []);
  const [feedType, setFeedType] = useState(feeding?.feed_type_id ?? '');
  const [fedOn, setFedOn] = useState(feeding?.fed_on ?? todayIso());
  const [amount, setAmount] = useState<number | null>(feeding?.amount ?? null);
  const [unit, setUnit] = useState<FeedUnit | ''>(feeding?.unit ?? '');
  const [mode, setMode] = useState<'per' | 'total'>('per');
  const [purpose, setPurpose] = useState<string>(feeding?.purpose ?? '');
  const [notes, setNotes] = useState(feeding?.notes ?? '');
  const typeValue = feedType || feedTypes.data?.[0]?.id || '';
  const currentType = feedTypes.data?.find((f) => f.id === typeValue);
  const unitValue: FeedUnit = unit || currentType?.unit || 'kg';
  const errors = formErrors(mutation.error, {
    colonies: ['colonies.'],
    amount: ['feeding.amount', 'quantity.'],
    feedType: ['feed_type.'],
  });
  const close = () => {
    mutation.reset();
    onClose();
  };
  const submit = () => {
    const body: FeedingFields = {
      feed_type_id: typeValue,
      fed_on: fedOn,
      amount: amount ?? 0,
      unit: unitValue,
      purpose: (purpose || null) as FeedingPurpose | null,
      notes: notes || null,
    };
    if (feeding) {
      update.mutate(
        { id: feeding.id, body, version: feeding.version },
        {
          onSuccess: () => {
            toast.show(t('apiary.feeding.saved'));
            close();
          },
        },
      );
    } else {
      record.mutate(
        { ...body, colony_ids: selected, amount_is_total: mode === 'total' },
        {
          onSuccess: (r) => {
            if (r.some(isQueued)) toast.show(t('offline.captured'), 'info');
            else toast.show(t('apiary.feeding.recorded', { count: r.length }));
            onRecorded?.();
            close();
          },
        },
      );
    }
  };
  const perColony =
    mode === 'total' && amount && selected.length > 0 ? amount / selected.length : undefined;

  return (
    <FormDialog
      open={open}
      onClose={close}
      drawer
      title={feeding ? t('apiary.feeding.editTitle') : t('apiary.feeding.recordTitle')}
      description={feeding?.colony.name}
      submitLabel={t('common.save')}
      onSubmit={submit}
      pending={mutation.isPending}
      error={errors.general}
    >
      {!feeding && (
        <ColonyPicker
          colonies={colonies.data ?? []}
          selected={selected}
          onChange={setSelected}
          error={errors.field.colonies}
          required
        />
      )}
      <div className={styles.row}>
        <FormField label={t('apiary.feeding.feedType')} required error={errors.field.feedType}>
          {(p) => (
            <Select
              {...p}
              value={typeValue}
              onChange={(e) => {
                setFeedType(e.target.value);
                setUnit('');
              }}
              options={(feedTypes.data ?? []).map((f) => ({ value: f.id, label: f.name }))}
            />
          )}
        </FormField>
        <FormField label={t('apiary.feeding.fedOn')} required>
          {(p) => <DatePicker {...p} value={fedOn} onChange={setFedOn} />}
        </FormField>
      </div>
      {!feeding && (
        <SegmentedControl
          label={t('apiary.feeding.amountMode')}
          value={mode}
          onChange={setMode}
          fullWidth
          options={[
            { value: 'per', label: t('apiary.feeding.perColony') },
            { value: 'total', label: t('apiary.feeding.total') },
          ]}
        />
      )}
      <div className={styles.row}>
        <FormField
          label={t('apiary.feeding.amount')}
          required
          error={errors.field.amount}
          hint={
            perColony
              ? t('apiary.feeding.splitHint', { amount: formatAmount(perColony), unit: unitValue })
              : undefined
          }
        >
          {(p) => (
            <NumberStepper
              {...p}
              value={amount}
              onChange={setAmount}
              min={0}
              step={0.5}
              decimals={3}
              unit={unitValue}
              labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
            />
          )}
        </FormField>
        <FormField label={t('apiary.feeding.unit')}>
          {(p) => (
            <Select
              {...p}
              value={unitValue}
              onChange={(e) => setUnit(e.target.value as FeedUnit)}
              options={[
                { value: 'kg', label: t('apiary.unit.kg') },
                { value: 'l', label: t('apiary.unit.l') },
              ]}
            />
          )}
        </FormField>
      </div>
      <FormField label={t('apiary.feeding.purpose')}>
        {(p) => (
          <Select
            {...p}
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            options={[
              { value: '', label: t('apiary.purpose.none') },
              ...PURPOSES.map((v) => ({ value: v, label: t(`apiary.purpose.${v}`) })),
            ]}
          />
        )}
      </FormField>
      <FormField label={t('apiary.feeding.notes')}>
        {(p) => (
          <Textarea {...p} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        )}
      </FormField>
    </FormDialog>
  );
}
